import { common } from '@/features/i18n/common';
import { t } from '@/features/i18n/utils';
import { SupplementBreadcrumbs } from '@/features/supplements/components/supplement-breadcrumbs';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { AnalysisStatus } from '@/features/workspaces/constants';
import { Icon } from '@/ui/components/icon';
import {
  Analytics01Icon,
  FileImportIcon,
  PlusSignIcon,
  Delete02Icon,
  Refresh01Icon,
} from '@hugeicons-pro/core-stroke-rounded';
import { BreadcrumbPage } from '@/ui/components/breadcrumb';
import { HeaderBackButton, HeaderChatButton, Header } from '@/ui/components/header';
import { useLocale } from 'use-intl';
import { MainHeader, MainHeaderBackButton, MainScrollArea } from '@/ui/components/main';
import { H1 } from '@/ui/components/heading';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyMedia,
  EmptyTitle,
} from '@/ui/components/empty';
import { ChangeIcon } from '@/ui/components/empty-icons';
import { Button } from '@/ui/components/button';
import { createChangeAssessmentOpenAtom } from '@/features/supplements/store';
import { useSetAtom } from 'jotai';
import { changeAssessmentListQuery } from '@/features/supplements/queries';
import { useState } from 'react';
import { fileUploadOpenAtom } from '@/features/documents/store';
import { useSuspenseQuery } from '@tanstack/react-query';
import { dateFormat, formatDate } from '@/dates';
import { CreateChangeAssessment } from '@/features/supplements/components/create-change-assessment';
import { ChangeAssessment } from '@/features/supplements/types';
import { FileUploadDialog } from '@/features/documents/components/file-upload';
import { Menu, MenuItem, MenuPopup } from '@/ui/components/menu';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogPopup,
  AlertDialogTitle,
} from '@/ui/components/alert-dialog';
import {
  useDeleteChangeAssessment,
  useRerunChangeAssessment,
} from '@/features/supplements/mutations';
import { useDocumentsIndexingStatus } from '@/features/documents/queries';
import { IndexingStatusAlerts } from '@/features/documents/components/indexing-status-alerts';
import { ChangeAssessmentBadge } from '@/features/supplements/components/change-assessment-badge';
import { Table, TableBody, TableRow, TableRowMenuTrigger } from '@/ui/components/table';
import { type ColumnDef, getCoreRowModel, Row, useReactTable } from '@tanstack/react-table';
import { DataTableHeader, DataTableCell } from '@/ui/components/data-table';

export const Route = createFileRoute(
  '/_authed/project/$projectId/workspaces/$workspaceId/contract-management/$supplementId/change-analysis/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const locale = useLocale();
  const { workspaceId, projectId, supplementId } = Route.useParams();
  const setCreateOpen = useSetAtom(createChangeAssessmentOpenAtom);
  const setUploadFilesOpen = useSetAtom(fileUploadOpenAtom);
  const navigate = useNavigate();

  const { data: assessments } = useSuspenseQuery(
    changeAssessmentListQuery(supplementId, workspaceId),
  );

  const { areDocumentsIndexed: isSupplementIndexed, hasNoDocuments } = useDocumentsIndexingStatus({
    documentType: 'supplement',
    id: supplementId,
  });
  const areDocumentsIndexed = !hasNoDocuments && isSupplementIndexed;

  const columns: ColumnDef<ChangeAssessment>[] = [
    {
      accessorKey: 'title',
      size: 200,
      meta: { primary: true },
      header: () => t({ en: 'Name', nb: 'Navn' }),
      cell: ({ row }) => row.original.title,
    },
    {
      accessorKey: 'documentFilename',
      header: () => t({ en: 'Document', nb: 'Dokument' }),
      cell: ({ row }) => row.original.documentFilename,
    },
    {
      accessorKey: 'verdict',
      header: () => t({ en: 'Status', nb: 'Status' }),
      cell: ({ row }) => <ChangeAssessmentBadge verdict={row.original.verdict} />,
    },
    {
      accessorKey: 'createdAt',
      header: () => t({ en: 'Date', nb: 'Dato' }),
      cell: ({ row }) => {
        if (row.original.status === AnalysisStatus.DONE && row.original.createdAt)
          return formatDate(row.original.createdAt, dateFormat.dayFirst, locale);
        if (row.original.status === AnalysisStatus.IN_PROGRESS)
          return (
            <span className="text-muted">{t({ en: 'Analysing...', nb: 'Analyserer...' })}</span>
          );
        if (row.original.status === AnalysisStatus.FAILED)
          return <span className="text-destructive">{t({ en: 'Failed', nb: 'Feilet' })}</span>;
        return null;
      },
    },
    {
      id: 'actions',
      size: 64,
      enableSorting: false,
      cell: ({ row }) => <MenuActions row={row} areDocumentsIndexed={areDocumentsIndexed} />,
      header: '',
    },
  ];

  const table = useReactTable({
    columns,
    data: assessments,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/workspaces/$workspaceId/contract-management/$supplementId"
          params={{ projectId, workspaceId, supplementId }}
        />
        <SupplementBreadcrumbs>
          <BreadcrumbPage>
            <Icon icon={Analytics01Icon} />
            {t({ en: 'Change analysis', nb: 'Endringsanalyse' })}
          </BreadcrumbPage>
        </SupplementBreadcrumbs>
        <HeaderChatButton />
      </Header>
      <MainScrollArea>
        <IndexingStatusAlerts resourceId={supplementId} documentType="supplement" />
        {assessments.length === 0 ? (
          <Empty>
            <EmptyMedia>
              <ChangeIcon />
            </EmptyMedia>
            <EmptyContent>
              <EmptyTitle>{t({ en: 'Change management', nb: 'Endringsanalyse' })}</EmptyTitle>
              <EmptyDescription>
                {t({
                  en: 'No change requests to display. Run your first change assessment to get started.',
                  nb: 'Ingen endringsanalyse å vise. Kjør din første endringsanalyse for å komme i gang.',
                })}
              </EmptyDescription>
              <div>
                <Button kind="with-icon" onClick={() => setCreateOpen(true)}>
                  <Icon icon={PlusSignIcon} />
                  {t({ en: 'Add a change / change request', nb: 'Legg til en endringsanalyse' })}
                </Button>
                <p className="text-muted my-2">{t(common.or)}</p>
                <Button
                  kind="with-icon"
                  variant="secondary"
                  onClick={() => setUploadFilesOpen(true)}
                >
                  <Icon icon={FileImportIcon} />
                  {t({
                    en: 'Upload / Import bulk changes',
                    nb: 'Last opp / importer masseendringsanalyser',
                  })}
                </Button>
              </div>
            </EmptyContent>
          </Empty>
        ) : (
          <>
            <MainHeader>
              <H1>
                <MainHeaderBackButton
                  to="/project/$projectId/workspaces/$workspaceId/contract-management/$supplementId"
                  params={{ projectId, workspaceId, supplementId }}
                />
                {t({ en: 'Change analysis', nb: 'Endringsanalyse' })}
              </H1>
              <Button
                className="max-md:w-full"
                kind="with-icon"
                onClick={() => setCreateOpen(true)}
              >
                <Icon icon={PlusSignIcon} />
                {t({ en: 'New change request', nb: 'Ny endringsanalyse' })}
              </Button>
            </MainHeader>
            <Table>
              <DataTableHeader table={table} />
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    data-clickable={row.original.status === AnalysisStatus.DONE ? '' : undefined}
                    className="data-clickable:cursor-pointer"
                    onClick={() => {
                      if (row.original.status !== AnalysisStatus.DONE) return;
                      navigate({
                        to: '/project/$projectId/workspaces/$workspaceId/contract-management/$supplementId/change-analysis/$assessmentId',
                        params: {
                          projectId,
                          workspaceId,
                          supplementId,
                          assessmentId: row.original.id,
                        },
                      });
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <DataTableCell key={cell.id} cell={cell} />
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </MainScrollArea>
      {assessments.length === 0 && (
        <FileUploadDialog resourceId={supplementId} resourceType="supplement" />
      )}
      <CreateChangeAssessment />
    </>
  );
}

function MenuActions({
  row,
  areDocumentsIndexed,
}: {
  row: Row<ChangeAssessment>;
  areDocumentsIndexed: boolean;
}) {
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [rerunAnalysisOpen, setRerunAnalysisOpen] = useState(false);

  const deleteAssessment = useDeleteChangeAssessment();
  const rerunAssessment = useRerunChangeAssessment();

  return (
    <div className="text-right" onClick={(e) => e.stopPropagation()}>
      <Menu>
        <TableRowMenuTrigger />
        <MenuPopup align="end">
          <MenuItem
            onClick={() => setRerunAnalysisOpen(true)}
            disabled={row.original.status === AnalysisStatus.IN_PROGRESS || !areDocumentsIndexed}
          >
            <Icon icon={Refresh01Icon} />
            {t({ en: 'Re-run', nb: 'Kjør på nytt' })}
          </MenuItem>
          <MenuItem variant="destructive" onClick={() => setIsDeleteOpen(true)}>
            <Icon icon={Delete02Icon} />
            {t(common.delete)}
          </MenuItem>
        </MenuPopup>
      </Menu>
      <AlertDialog open={rerunAnalysisOpen} onOpenChange={setRerunAnalysisOpen}>
        <AlertDialogPopup>
          <AlertDialogTitle>{t(common.reRunAnalysis)}?</AlertDialogTitle>
          <AlertDialogDescription>{t(common.reRunAnalysisDescription)}</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel />
            <Button
              pending={rerunAssessment.isPending}
              onClick={() =>
                rerunAssessment.mutate(
                  { name: row.original.title, docId: row.original.docId },
                  { onSuccess: () => setRerunAnalysisOpen(false) },
                )
              }
            >
              {t(common.reRunAnalysis)}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogPopup>
          <AlertDialogTitle>
            {t({ en: 'Delete change request?', nb: 'Slett endringsforespørsel?' })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t({
              en: 'Are you sure you want to delete this change request?',
              nb: 'Er du sikker på at du vil slette denne endringsforespørselen?',
            })}
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteAssessment.isPending} />
            <Button
              variant="destructive"
              pending={deleteAssessment.isPending}
              onClick={() =>
                deleteAssessment.mutate(row.original.id, {
                  onSuccess: () => setIsDeleteOpen(false),
                })
              }
            >
              {t(common.delete)}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </div>
  );
}
